import SEO from '../components/SEO';
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Heart, Recycle, Users, ArrowRight, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import OptimizedImage from '../components/OptimizedImage';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Professionnalisme',
      description: 'Équipe formée, assurée et équipée pour tous types de débarras'
    },
    {
      icon: Recycle,
      title: 'Respect de l\'environnement',
      description: 'Tri sélectif, recyclage et valorisation systématiques'
    },
    {
      icon: Heart,
      title: 'Écoute et empathie',
      description: 'Accompagnement respectueux dans les situations sensibles'
    },
    {
      icon: Users,
      title: 'Proximité',
      description: 'Entreprise locale au service de la région Savoie-Haute-Savoie'
    }
  ];

  const expertise = [
    'Plus de 10 ans d\'expérience dans le débarras',
    'Interventions pour particuliers et professionnels',
    'Partenariat avec associations locales',
    'Équipe formée et professionnelle',
    'Matériel adapté et véhicules équipés',
    'Respect des normes environnementales'
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="DEBECO : entreprise de débarras responsable à Annecy"
        description="Découvrez l'équipe DEBECO, nos valeurs et notre engagement pour un débarras professionnel, écologique et solidaire en Savoie et Haute-Savoie."
        canonical="/a-propos"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "DEBECO",
          "alternateName": "DEBECO",
          "url": "https://debeco.fr",
          "telephone": "+33451401617",
          "email": "contact@debeco.fr",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "4 Rue des Frères de Montgolfier",
            "addressLocality": "Annecy",
            "postalCode": "74600",
            "addressCountry": "FR"
          },
          "areaServed": ["Haute-Savoie", "Savoie", "Ain"]
        }}
      />
      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">À propos de DEBECO</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Votre partenaire de confiance pour le débarras et le recyclage responsable
          </p>
        </div>
      </section>

      {/* Team Section - FIRST */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Notre équipe</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Les fondateurs de DEBECO à votre service</p>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* John Thomas PICCAMIGLIO */}
            <Card className="border-2 hover:border-emerald-500 hover:shadow-2xl transition-all">
              <CardContent className="p-10 text-center">
                <div className="w-56 h-56 mx-auto mb-8 rounded-full overflow-hidden border-4 border-emerald-500 shadow-xl">
                  <OptimizedImage 
                    src="/images/jt-portrait.jpg"
                    webpSrc="/images/jt-portrait.webp"
                    alt="Portrait de John Thomas PICCAMIGLIO, co-fondateur de DEBECO, entreprise de débarras en Savoie"
                    className="w-full h-full object-cover object-top"
                    width="224"
                    height="224"
                    loading="eager"
                    data-testid="jt-portrait"
                  />
                </div>
                <h3 className="text-2xl font-bold text-blue-900 mb-2">John Thomas PICCAMIGLIO</h3>
                <p className="text-emerald-600 font-semibold mb-4">Fondateur</p>
                <p className="text-gray-600 leading-relaxed">
                  Passionné par l'économie circulaire et l'écologie, John Thomas a créé DEBECO avec la vision de rendre le débarras plus responsable et respectueux de l'environnement.
                </p>
              </CardContent>
            </Card>

            {/* Nicolas ZAGO */}
            <Card className="border-2 hover:border-emerald-500 hover:shadow-2xl transition-all">
              <CardContent className="p-10 text-center">
                <div className="w-56 h-56 mx-auto mb-8 rounded-full overflow-hidden border-4 border-emerald-500 shadow-xl">
                  <OptimizedImage 
                    src="/images/nico-portrait.jpg"
                    webpSrc="/images/nico-portrait.webp"
                    alt="Portrait de Nicolas ZAGO, co-fondateur de DEBECO, expert en logistique de débarras"
                    className="w-full h-full object-cover object-top"
                    width="224"
                    height="224"
                    loading="eager"
                    data-testid="nico-portrait"
                  />
                </div>
                <h3 className="text-2xl font-bold text-blue-900 mb-2">Nicolas ZAGO</h3>
                <p className="text-emerald-600 font-semibold mb-4">Fondateur</p>
                <p className="text-gray-600 leading-relaxed">
                  Expert en logistique et gestion d'équipe, Nicolas apporte son savoir-faire pour assurer des interventions rapides et professionnelles dans toute la région.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Story - SECOND */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">Notre histoire</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>DEBECO</strong> est une entreprise locale spécialisée dans le débarras de mobilier depuis plusieurs années. Basés à Annecy, nous intervenons en Savoie, Haute-Savoie et dans l'Ain.
                  </p>
                  <p>
                    Notre mission : proposer un service de débarras professionnel, rapide et respectueux de l'environnement. Que ce soit pour un déménagement, une succession, ou simplement pour désencombrer votre espace, nous vous accompagnons avec écoute et efficacité.
                  </p>
                  <p>
                    Nous mettons l'accent sur le <strong>recyclage</strong> et la <strong>valorisation</strong> : ce qui peut avoir une seconde vie est donné ou revendu, dans une logique d'économie circulaire.
                  </p>
                </div>
              </div>
              
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="/images/brocante.jpg"
                  webpSrc="/images/brocante.webp"
                  alt="Stand de brocante DEBECO avec des meubles et objets récupérés lors de débarras, prêts pour la revente et le réemploi"
                  className="w-full h-full object-cover"
                  width="1066"
                  height="1600"
                  loading="lazy"
                  data-testid="brocante-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values - THIRD */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Nos valeurs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Ce qui nous anime au quotidien</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all text-center">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-bold text-lg text-blue-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Banner - Between Values and Expertise */}
      <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-emerald-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl md:text-2xl lg:text-3xl text-white leading-relaxed tracking-wide">
              Nous travaillons en <span className="font-bold text-emerald-300">équipe interne</span>, sans sous-traitants. C'est notre manière de garantir la <span className="font-bold text-emerald-300">qualité</span>, la <span className="font-bold text-emerald-300">discrétion</span> et la <span className="font-bold text-emerald-300">confiance</span> à chaque débarras.
            </p>
          </div>
        </div>
      </section>

      {/* Expertise - FOURTH */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="/images/camion.jpg"
                  webpSrc="/images/camion.webp"
                  alt="Camion utilitaire DEBECO utilisé pour les interventions de débarras de mobilier en Savoie et Haute-Savoie"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="800"
                  loading="lazy"
                  data-testid="camion-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/60 to-transparent"></div>
              </div>

              <div className="order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">Notre expertise</h2>
                <ul className="space-y-3">
                  {expertise.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement écologique - FIFTH */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-emerald-500 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 p-6">
                <h2 className="text-3xl font-bold text-white text-center">Notre engagement écologique</h2>
              </div>
              <CardContent className="p-8">
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Chez <strong>DEBECO</strong>, nous sommes convaincus que le débarras peut être <strong>écologique et responsable</strong>. C'est pourquoi nous mettons en place une gestion rigoureuse de tous les objets récupérés :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Recycle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Tri sélectif systématique</strong> de tous les matériaux</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Recycle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Don aux associations</strong> (Emmaüs, Croix-Rouge) pour les objets en bon état</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Recycle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Réutilisation d'objets en état d'usage</strong> pour leur donner une seconde vie</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Recycle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Recyclage en déchetterie agréée</strong> pour le reste</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Découvrez nos services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/services" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Tous nos services de débarras</p>
              <p className="text-sm text-gray-600 mt-1">Maison, appartement, cave, succession, professionnel</p>
            </Link>
            <Link to="/zones" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Nos zones d'intervention</p>
              <p className="text-sm text-gray-600 mt-1">Savoie, Haute-Savoie et Ain</p>
            </Link>
            <Link to="/faq" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Questions fréquentes</p>
              <p className="text-sm text-gray-600 mt-1">Tarifs, délais, déroulement de l'intervention</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Travaillons ensemble</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Faites confiance à DEBECO pour un débarras professionnel et responsable
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Contactez-nous
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
